/**
 * Dependencies
 */
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import debounce from 'lodash/debounce';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import set from 'lodash/set';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import cloneDeep from 'lodash/cloneDeep';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import isEqual from 'lodash/isEqual';
import querystring from 'query-string';
import classNames from 'classnames';

import nds from '@nerdwallet/base-styles/classes';
import ConfigBuilder, { getMacroKey } from '@nerdwallet/react-config-builder';
import { Box, Button, Text } from '@nerdwallet/currency';
import Trash from '@nerdwallet/react-icon/gizmo/Trash';
import Up from '@nerdwallet/react-icon/gizmo/Up';
import Down from '@nerdwallet/react-icon/gizmo/Down';
import Accordion from '@nerdwallet/react-accordion';
import { formSubmit } from '@nerdwallet/nw-client-lib/tracking';

import { makeRawUpdate } from '../../lib/actions/main-actions';
import {
  stringInputSpec,
  stringTextAreaSpec,
  stringSelectSpec,
  boolCheckboxSpec,
  repeatable,
  findPathInParent,
  generateSettingUIConfigFromSpec,
} from '../../lib/editing-configs';
import { getValidationFn } from '../../lib/validations';
import { valueFromConfigBuilderCheckbox } from '../../lib/helpers';

import { receiveForm, getRepeatablePath } from './actions';
// TODO: fix import cycle
// eslint-disable-next-line import/no-cycle
import {
  addRepeatable,
  deleteRepeatable,
  moveRepeatable,
} from '../../containers/builder/actions';

import './grid-form.less';
import { Draggable } from './draggable';

/*
 * Config-based, grid-stacking form.
 * If provided a redirectUrl, will stringify form fields and values and redirect to the url provided.
 * If provided an onClick, will pass form as an object to the onClick.
 * Current implementation holds form state internally.
 */
class GridForm extends Component {
  static withConfigBasedValidation(elConfig) {
    const { validationType } = elConfig;
    const keyToValidate = get(elConfig.keys, '0.key');
    const validationFn = getValidationFn(validationType, keyToValidate);

    return {
      getValidationConfig: validationFn,

      // Explicitly defined fn gets precedent
      ...elConfig,
    };
  }

  constructor(props) {
    super(props);

    // Data structure is set in props
    this.state = this.props.initialState;

    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.handleAttemptedSubmit = debounce(this.handleAttemptedSubmit, 100);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.attemptedSubmit !== this.state.attemptedSubmit) {
      this.handleAttemptedSubmit();
    }

    const newFormConfig = !isEqual(prevProps.formConfig, this.props.formConfig);
    const newInitialState = !isEqual(
      prevProps.initialState,
      this.props.initialState
    );

    if (newFormConfig || newInitialState) {
      this.reloadState();
    }
  }

  handleAttemptedSubmit() {
    const { form } = this.state;
    const { formName, formConfig, dispatch } = this.props;

    if (this.isFormValid()) {
      const { redirectUrl, onClick } = this.props;

      // Log submission
      formSubmit({
        formName,
        fieldName: 'complete',
        fieldValue: 'complete',
      });

      // Pass data to given onClick
      if (onClick) onClick(form);

      // Make raw updates for each field if needed
      formConfig.forEach((fieldConfig) => {
        if (fieldConfig.outputKey) {
          const formKey = get(fieldConfig, 'keys.0.key');
          const value = get(this.state, formKey);
          const storePath = fieldConfig.outputKey;

          dispatch(makeRawUpdate(storePath, value));
        }
      });

      // dispatch form data to forms store
      dispatch(receiveForm(formName, form));

      // Redirect to given url with stringified form params
      if (window && redirectUrl) {
        const query = querystring.stringify(form);
        window.location = `${redirectUrl}?${query}`;
      }
    } else {
      // Log incomplete submission attempt
      formSubmit({
        formName,
        fieldName: 'incomplete',
        fieldValue: `submitted ${Object.keys(form).join(', ')}`,
      });
    }
  }

  onChange(config, value) {
    const key = getMacroKey(config, 'value');

    const parsedValue =
      config.viewType === 'checkbox'
        ? valueFromConfigBuilderCheckbox(value)
        : value;

    // eslint-disable-next-line react/no-access-state-in-setstate
    this.setState(set(cloneDeep(this.state), key, parsedValue));

    if (this.props.liveUpdate) {
      // eslint-disable-next-line react/no-access-state-in-setstate
      this.setState({ attemptedSubmit: (this.state.attemptedSubmit || 0) + 1 });
    }
  }

  onClick() {
    // Let validations know the user is trying to submit the form so we can
    // verbosely invalidate empty required fields.
    // eslint-disable-next-line react/no-access-state-in-setstate
    this.setState({ attemptedSubmit: (this.state.attemptedSubmit || 0) + 1 });
  }

  getAddRepeatableFn(key) {
    const path = getRepeatablePath(key, this.props.initialState);

    return () => {
      const nextRepeatableIndex = get(this.props.initialState, key, []).length;
      const defaultRepeatable =
        get(this.state, 'form.type') === 'GridForm'
          ? {
              viewType: 'input',
              formatType: 'none',
              subType: 'text',
              value: '{{0}}',
              keys: [
                {
                  key: `form.attribute-${nextRepeatableIndex}`,
                  format: 'string',
                },
              ],
            }
          : {};
      this.props.dispatch(addRepeatable(path, defaultRepeatable));
    };
  }

  getDeleteRepeatableFn(key, index) {
    const path = getRepeatablePath(key, this.props.initialState);

    return () => this.props.dispatch(deleteRepeatable(path, index));
  }

  getRepeatableTitle({ value, fields, index, singularLabel, options }) {
    if (typeof options.label === 'function') {
      const label = options.label(value);

      if (label) {
        return label;
      }
    } else if (typeof options.label === 'string' && value[options.label]) {
      return value[options.label];
    }

    // we want to show the value of the first field in the repeatable as the title of the panel
    const firstKey = get(fields, '0.keys.0.key', '_');
    const firstField = get(this.state, firstKey);
    // but if the first field is another repeatable or the first field is empty, just show a generic title
    return firstField && typeof firstField === 'string'
      ? firstField
      : `${singularLabel} #${index + 1}`;
  }

  moveRepeatable(key, from, to) {
    const path = getRepeatablePath(key, this.props.initialState);
    return () => this.props.dispatch(moveRepeatable(path, from, to));
  }

  isFormValid() {
    const { formConfig } = this.props;
    const data = this.state;

    const hydratedConfig = formConfig.map(GridForm.withConfigBasedValidation);

    // Any fields invalid?
    return !hydratedConfig.some(
      ({ getValidationConfig }) =>
        getValidationConfig && !getValidationConfig(data).isValid
    );
  }

  reloadState() {
    this.setState(this.props.initialState);
  }

  repeatableUI(data, key, i) {
    const moveUp = this.moveRepeatable(key, i, i - 1);
    const moveDown = this.moveRepeatable(key, i, i + 1);
    return (
      <div className="ui">
        {i > 0 ? <Up onClick={moveUp} /> : null}
        {i < data.length - 1 ? <Down onClick={moveDown} /> : null}
        <Trash
          onClick={this.getDeleteRepeatableFn(key, i)}
          size={15}
          title="delete this element"
          className="trash"
        />
      </div>
    );
  }

  renderRepeatableGroup({ label, singularLabel, elements, keys, options }) {
    const { key } = keys[0];
    const data = get(this.state, key, []);
    const repeatableIsEditable = get(options, 'repeatableIsEditable', true);

    return (
      <div className="grid-form-repeatable" key={`repeatable-${key}`}>
        <Text bold size="small">
          {label}
        </Text>
        <Draggable
          reorder={(event) => {
            this.moveRepeatable(key, event.from, event.to)();
          }}
        >
          {data.map((item, index) => {
            const processedElements = elements(index).map(
              generateSettingUIConfigFromSpec
            );
            const fields = processedElements.map(findPathInParent(key, index));
            const panelTitle = this.getRepeatableTitle({
              value: item,
              fields,
              index,
              singularLabel,
              options,
            });
            return (
              <Accordion
                key={`panel-${label}-${item.$unique_key || index}`}
                enableTracking
                items={[
                  {
                    title: (
                      <Text inline size="small">
                        {panelTitle}
                      </Text>
                    ),
                    children: (
                      <Fragment>
                        {repeatableIsEditable
                          ? this.repeatableUI(data, key, index)
                          : null}
                        {this.renderForm(fields)}
                      </Fragment>
                    ),
                  },
                ]}
              />
            );
          })}
        </Draggable>
        {repeatableIsEditable ? (
          <Fragment>
            <Button
              className={nds('margin-bottom-3')}
              onClick={this.getAddRepeatableFn(key)}
              size="small"
              block
            >
              Add {singularLabel}
            </Button>
          </Fragment>
        ) : null}
      </div>
    );
  }

  renderGroup({ label, elements, options }) {
    return (
      <div className="grid-form-group">
        <Accordion
          title={label}
          items={[
            {
              title: label,
              children: this.renderForm(
                elements.map(generateSettingUIConfigFromSpec)
              ),
              initiallyExpanded: !!options?.expanded,
            },
          ]}
        />
      </div>
    );
  }

  renderRow({ elements }) {
    // TODO: Not implemented
    return (
      <Box>
        {this.renderForm(elements.map(generateSettingUIConfigFromSpec))}
      </Box>
    );
  }

  renderForm(formConfig) {
    return formConfig.map((elConfig, index) => {
      switch (elConfig.viewType) {
        case 'group': {
          return this.renderGroup(elConfig);
        }
        case 'repeatable': {
          return this.renderRepeatableGroup(elConfig);
        }
        case 'row': {
          return this.renderRow(elConfig);
        }
        default: {
          return (
            <ConfigBuilder
              data={this.state}
              className={elConfig.viewType}
              viewConfig={GridForm.withConfigBasedValidation(elConfig)}
              onChange={this.onChange}
              formName={this.props.formName}
              // eslint-disable-next-line react/no-array-index-key
              key={`${get(elConfig, 'viewType')}-${index}`}
            />
          );
        }
      }
    });
  }

  renderButton() {
    const { buttonText, buttonSubText } = this.props;
    const disabled = !this.isFormValid();

    return (
      <div>
        <Button primary block onClick={this.onClick} disabled={disabled}>
          {buttonText}
        </Button>
        <div className="grid-form-button-subtext">{buttonSubText}</div>
      </div>
    );
  }

  render() {
    const { formConfig, liveUpdate, orientation, columns } = this.props;

    return (
      <div className="grid-form">
        <div
          className={classNames('grid-form-container', {
            'grid-form-container-1-col': orientation === 'vertical',
            [`grid-form-container-${columns}-col`]: orientation !== 'vertical',
          })}
        >
          {this.renderForm(formConfig)}
          {!liveUpdate && this.renderButton()}
        </div>
      </div>
    );
  }
}

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
GridForm.displayName = 'GridForm';

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
GridForm.propTypes = {
  buttonSubText: PropTypes.string,
  buttonText: PropTypes.string,
  columns: PropTypes.string,

  // Field configs based on ConfigBuilder API, with a few modifications
  formConfig: PropTypes.arrayOf(
    PropTypes.shape({
      // The key on the store you want this field to control.
      // If this key is wiped out by a reducer function, the value will not persist.
      outputKey: PropTypes.string,

      /* All following props are ala ConfigBuilder */

      // Input type
      viewType: PropTypes.oneOf([
        'input',
        'radio',
        'select',
        'checkbox',
        'textarea',
        'repeatable',
        // There are more types, but not yet supported in editor
      ]),

      // Shown above input
      label: PropTypes.string,

      // Renders help tooltip
      help: PropTypes.string,

      // Renders placeholder
      placeholder: PropTypes.string,

      // How data should be rendered in the input
      formatType: PropTypes.oneOf([
        'currency',
        'text',
        'none',
        'number',
        'date',
      ]),

      // How data should be stored
      subType: PropTypes.oneOf(['currency', 'text', 'number', 'date']),

      // Populated by getMacroKey. Typically {{0}}, ie first macro value. Don't mess with this.
      value: PropTypes.string,

      // Select menu options
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
          ]),
        })
      ),

      // Keys on the form state that this form should control, and their data format
      keys: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          format: PropTypes.oneOf(['text', 'currency', 'number', 'date']),
        })
      ),

      // See ConfigBuilder
      getValidationConfig: PropTypes.func,

      // Used to set `getValidationConfig` (above) in a config-based way.
      // Provide a type of validation (e.g. 'zip') and that validation will be used
      // if it is available in the validations lib.
      validationType: PropTypes.string,
    })
  ),
  formName: PropTypes.string,
  initialState: PropTypes.object,
  liveUpdate: PropTypes.bool,
  onClick: PropTypes.func,
  redirectUrl: PropTypes.string,
  dispatch: PropTypes.func,
  orientation: PropTypes.oneOf(['vertical', 'horizontal']),
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
GridForm.defaultProps = {
  initialState: {},
  buttonText: 'Click me',
  buttonSubText: '',
  formConfig: [],
  liveUpdate: false,
  columns: '3',
};

GridForm.getEditingConfig = () => [
  stringInputSpec('formName', 'Form Name (for tracking)', {
    help: `E.g., cd-table-filter, or quotacy-data. Name can be simple,
    since you'll filter by url then by form name.`,
  }),
  boolCheckboxSpec('liveUpdate', 'Auto Submit?', {
    help: `Click this if you want to hide the submit button.
    The form will submit as soon as it is valid.
    E.g. use case: if this form is a filter for something else.`,
  }),
  stringInputSpec('buttonText', 'Button text'),
  stringInputSpec('buttonSubText', 'Button subtext'),
  stringSelectSpec(
    'columns',
    'Number of columns',
    [
      {
        value: '4',
        label: '4',
      },
      {
        value: '3',
        label: '3',
      },
      {
        value: '1',
        label: '1',
      },
    ],
    {
      help: 'Number of columns in layout on desktop',
    }
  ),
  repeatable('formConfig', 'Fields', 'Field', () => [
    stringInputSpec('title', 'Field Label'),
    stringTextAreaSpec('help', 'Help Bubble'),
    stringSelectSpec(
      'viewType',
      'Input Type',
      [
        {
          value: '',
          label: 'Select One',
        },
        {
          value: 'input',
          label: 'Input',
        },
        {
          value: 'select',
          label: 'Select',
        },
        {
          value: 'textarea',
          label: 'Text Area',
        },
        {
          value: 'checkbox',
          label: 'Checkbox',
        },
        {
          value: 'radio',
          label: 'Radio',
        },
      ],
      {
        help: 'Type of UI for this field',
      }
    ),
    stringSelectSpec(
      'formatType',
      'Show as...',
      [
        {
          value: '',
          label: 'Select One',
        },
        {
          value: 'none',
          label: 'Text',
        },
        {
          value: 'currency',
          label: 'Currency',
        },
        {
          value: 'number',
          label: 'Number',
        },
        {
          value: 'date',
          label: 'Date',
        },
      ],
      {
        help: 'How do you want this field to be formatted?',
      }
    ),
    stringSelectSpec(
      'subType',
      'Store data as...',
      [
        {
          value: '',
          label: 'Select one',
        },
        {
          value: 'text',
          label: 'Text',
        },
        {
          value: 'number',
          label: 'Number',
        },
        {
          value: 'date',
          label: 'Date',
        },
      ],
      {
        help: 'How do you want the data to actually be stored (vs displayed)?',
      }
    ),
    repeatable('items', 'Options (for select)', 'Option', () => [
      stringInputSpec('label', 'Label', {
        help: 'What shows for this option',
      }),
      stringInputSpec('value', 'Value', {
        help: 'What is stored for this option',
      }),
    ]),
    repeatable('keys', 'Form Key', 'Key', () => [
      stringInputSpec('key', 'Key', {
        help: `The component state key to use for storing the data.
        Namespace under 'form.<key>' unless you need to do otherwise.
        E.g. form.zip`,
      }),
      stringSelectSpec(
        'format',
        'Format',
        [
          {
            value: 'currency',
            label: 'Currency',
          },
          {
            value: 'number',
            label: 'Number',
          },
          {
            value: 'string',
            label: 'Text',
          },
          {
            value: 'date',
            label: 'Date',
          },
        ],
        {
          help: 'How this value should be stored (again.)',
        }
      ),
    ]),
    stringInputSpec('outputKey', 'Store Key', {
      help: `If you want to control a value on the redux store, enter the key
      here. Note, a reducer can still overwrite or remove your value.`,
    }),
  ]),
];

export default connect()(GridForm);
